import * as React from "react";

/**
 * Runs `reset` whenever `open` becomes true — used by dialogs/modals to clear
 * their form state each time they reopen. Callers may pass an inline function:
 * `reset` is read through a ref so that a new function on each render does not
 * become an effect dependency, which would re-run the effect and wipe state on
 * every keystroke.
 *
 * Pass `resetKey` when the reset depends on data that may arrive after the modal
 * opens (e.g. an async-fetched form schema). The reset re-runs whenever the key
 * changes, so default values are picked up once the data loads — not just on the
 * initial open.
 *
 * @example
 * function MyDialog({ open }: { open: boolean }) {
 *   const [value, setValue] = React.useState("");
 *   useResetOnOpen(open, () => setValue(""));
 *   // ...
 * }
 */
export function useResetOnOpen(
  open: boolean,
  reset: () => void,
  resetKey?: unknown,
) {
  const resetRef = React.useRef(reset);

  // Layout effects run before passive effects, so the ref is up to date by the
  // time the open effect below reads it.
  React.useLayoutEffect(() => {
    resetRef.current = reset;
  });

  React.useEffect(() => {
    if (open) {
      resetRef.current();
    }
  }, [open, resetKey]);
}
